import { useEffect, useState } from 'react';
import { formatMoney } from '@domain';
import { loadDocumentDetail, loadPrintBranding, type BrandingFooter, type DataSource } from '../api.js';
import { BandedSheet } from './PrintBanded.js';
import { EMPTY_PRESENTATION, type Presentation, type XeroDocument } from './printTypes.js';
import type { Lang, Translate } from '../i18n/index.js';
import { theme } from '../theme.js';

/**
 * A document laid out for paper, printed by the browser.
 *
 * WHY THE BROWSER AND NOT A PDF LIBRARY
 * Arabic is the whole reason. Letters change shape according to their
 * neighbours and join into words, and a line mixing Arabic with Latin names and
 * Western figures has to be reordered for display without reordering the
 * meaning. PDF libraries do not do either; they draw the characters in the
 * order given, unjoined, and the result is subtly and embarrassingly wrong in
 * a way nobody notices until a client does.
 *
 * A browser does both, correctly, every time it draws a page. So the layout is
 * HTML and the PDF comes from Print → Save as PDF. The artefact is the same; the
 * typography is right.
 *
 * WHAT IS ON IT
 * The client's own logo, and both languages where a document is read by two
 * audiences: the client keeps their books in Arabic and their customers may
 * not. Figures stay in Western digits throughout, as everywhere else in this
 * product, because that is what a bank and a tax authority expect.
 */

const TITLES: Record<string, { en: string; ar: string }> = {
  invoice: { en: 'Invoice', ar: 'فاتورة' },
  bill: { en: 'Bill', ar: 'فاتورة مشتريات' },
  quote: { en: 'Quote', ar: 'عرض سعر' },
  po: { en: 'Purchase order', ar: 'أمر شراء' },
};

/** A date as it should read on paper, never as Xero's own wrapper. */
function day(value: string | undefined): string {
  if (!value) return '';
  return value.slice(0, 10);
}

export function PrintDocument({
  kind,
  id,
  lang,
  source,
  t,
  onClose,
}: {
  kind: string;
  id: string;
  lang: Lang;
  source: DataSource;
  t: Translate;
  onClose: () => void;
}) {
  const [doc, setDoc] = useState<XeroDocument | null>(null);
  const [organisation, setOrganisation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [footer, setFooter] = useState<BrandingFooter>({ email: '', phone: '', address: '' });
  const [presentation, setPresentation] = useState<Presentation>(EMPTY_PRESENTATION);

  // Starts as the interface language and can be changed for this document
  // alone: the person printing may not read what the customer reads.
  const [sheetLang, setSheetLang] = useState<Lang>(lang);
  useEffect(() => setSheetLang(lang), [lang]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [detail, branding] = await Promise.all([
          loadDocumentDetail(kind, id, source),
          loadPrintBranding(source),
        ]);
        if (cancelled) return;
        setDoc(detail.document as XeroDocument);
        setOrganisation(detail.organisation);
        setLogoUrl(branding.logoUrl);
        setFooter(branding.footer);
        setPresentation(branding.presentation);
      } catch {
        if (!cancelled) setError('That document could not be read from Xero.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [kind, id, source]);

  const title = TITLES[kind] ?? { en: 'Document', ar: 'مستند' };
  const currency = doc?.CurrencyCode ?? '';
  const lines = doc?.LineItems ?? [];
  const bilingual = (en: string, ar: string) => (sheetLang === 'ar' ? `${ar} · ${en}` : `${en} · ${ar}`);

  return (
    <div style={{ background: theme.canvas, minHeight: '100vh', padding: '20px' }}>
      {/*
        Everything outside the sheet disappears when printing, and the sheet
        loses its shadow and margins so it sits on the page rather than on a
        picture of a page.
      */}
      <style>{`
        @media print {
          @page { size: A4; margin: 14mm; }
          body { background: #fff !important; }
          .no-print { display: none !important; }
          .sheet { box-shadow: none !important; margin: 0 !important; width: auto !important; border-radius: 0 !important; }
          /* A layout whose head and foot are coloured bands is not recognisable
             without them, and browsers drop backgrounds when printing unless
             told otherwise. */
          .band, .sheet [style*="border-radius: 999px"] {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .sheet tr { break-inside: avoid; }
          thead { display: table-header-group; }
        }
      `}</style>

      <div
        className="no-print"
        style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}
      >
        <button
          type="button"
          onClick={onClose}
          style={{
            background: 'none',
            border: 0,
            color: theme.ink,
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            padding: 0,
          }}
        >
          {t('← Back')}
        </button>
        <div style={{ flex: 1 }} />

        <div style={{ display: 'flex', gap: '4px', marginInlineEnd: '10px' }}>
          {(['en', 'ar'] as const).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setSheetLang(option)}
              style={{
                background: sheetLang === option ? theme.nav : 'transparent',
                color: sheetLang === option ? '#fff' : theme.textMuted,
                border: `1px solid ${sheetLang === option ? theme.nav : theme.borderStrong}`,
                borderRadius: theme.radiusControl,
                padding: '7px 13px',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {option === 'en' ? 'EN' : 'AR'}
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={() => window.print()}
          disabled={!doc}
          style={{
            background: doc ? theme.nav : theme.borderStrong,
            color: '#fff',
            border: 0,
            borderRadius: theme.radiusControl,
            padding: '10px 18px',
            fontSize: '13px',
            fontWeight: 600,
            cursor: doc ? 'pointer' : 'default',
          }}
        >
          {t('Print or save as PDF')}
        </button>
      </div>

      {!logoUrl && (
        <div className="no-print" style={{ fontSize: '12px', color: theme.textMuted, marginBottom: '12px' }}>
          {t('No logo has been set for this organisation. Admin → Branding.')}
        </div>
      )}

      {error && (
        <div className="no-print" style={{ color: theme.badDeep, fontWeight: 600, fontSize: '13px' }}>
          {t(error)}
        </div>
      )}

      {presentation.template === 'banded' ? (
        <BandedSheet
          doc={doc}
          organisation={organisation}
          logoUrl={logoUrl}
          presentation={presentation}
          footer={footer}
          lang={sheetLang}
        />
      ) : (
      <div
        className="sheet"
        dir={sheetLang === 'ar' ? 'rtl' : 'ltr'}
        style={{
          background: '#fff',
          maxWidth: '820px',
          margin: '0 auto',
          padding: '34px 38px',
          boxShadow: theme.shadowCard,
          borderRadius: '4px',
          fontFamily: sheetLang === 'ar' ? theme.fontArabic : theme.fontBody,
          color: '#1a1a1a',
          fontSize: '13px',
          lineHeight: 1.6,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '20px', marginBottom: '26px' }}>
          <div style={{ minWidth: 0 }}>
            {logoUrl && (
              <img
                src={logoUrl}
                alt=""
                style={{ maxHeight: '64px', maxWidth: '240px', objectFit: 'contain', marginBottom: '10px' }}
              />
            )}
            <div style={{ fontSize: '17px', fontWeight: 700 }}>{organisation}</div>
          </div>

          <div style={{ textAlign: 'end', flex: '0 0 auto' }}>
            <div style={{ fontSize: '22px', fontWeight: 700, letterSpacing: '-.01em' }}>
              {sheetLang === 'ar' ? title.ar : title.en}
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
              {sheetLang === 'ar' ? title.en : title.ar}
            </div>
            {doc?.InvoiceNumber && (
              <div style={{ fontFamily: theme.fontMono, fontSize: '13px', marginTop: '8px' }} dir="ltr">
                {doc.InvoiceNumber}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '26px', marginBottom: '22px' }}>
          <div style={{ flex: '1 1 240px', minWidth: 0 }}>
            <Label>{bilingual('To', 'إلى')}</Label>
            <div style={{ fontWeight: 600 }}>{doc?.Contact?.Name ?? ''}</div>
            {doc?.Contact?.EmailAddress && (
              <div style={{ color: '#666', fontSize: '12px' }} dir="ltr">
                {doc.Contact.EmailAddress}
              </div>
            )}
          </div>

          <div style={{ flex: '0 1 auto', display: 'flex', gap: '26px', flexWrap: 'wrap' }}>
            <Field label={bilingual('Date', 'التاريخ')} value={day(doc?.DateString)} />
            {doc?.DueDateString && <Field label={bilingual('Due', 'الاستحقاق')} value={day(doc.DueDateString)} />}
            {doc?.Reference && <Field label={bilingual('Reference', 'المرجع')} value={doc.Reference} />}
          </div>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '18px' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #1a1a1a' }}>
              <Th align="start">{bilingual('Description', 'الوصف')}</Th>
              <Th align="end" width="70px">{bilingual('Qty', 'الكمية')}</Th>
              <Th align="end" width="110px">{bilingual('Unit price', 'سعر الوحدة')}</Th>
              <Th align="end" width="120px">{bilingual('Amount', 'المبلغ')}</Th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => (
              <tr key={index} style={{ borderBottom: '1px solid #e6e6e6' }}>
                <td style={{ padding: '9px 6px', verticalAlign: 'top' }}>{line.Description ?? ''}</td>
                <Td>{line.Quantity ?? ''}</Td>
                <Td>{line.UnitAmount === undefined ? '' : formatMoney(line.UnitAmount)}</Td>
                <Td>{line.LineAmount === undefined ? '' : formatMoney(line.LineAmount)}</Td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <table style={{ minWidth: '280px', borderCollapse: 'collapse' }}>
            <tbody>
              <Total label={bilingual('Subtotal', 'المجموع الفرعي')} value={doc?.SubTotal} currency={currency} />
              <Total label={bilingual('Tax', 'الضريبة')} value={doc?.TotalTax} currency={currency} />
              <Total label={bilingual('Total', 'الإجمالي')} value={doc?.Total} currency={currency} strong />
              {typeof doc?.AmountPaid === 'number' && doc.AmountPaid > 0 && (
                <Total label={bilingual('Paid', 'المدفوع')} value={doc.AmountPaid} currency={currency} />
              )}
              {typeof doc?.AmountDue === 'number' && (
                <Total label={bilingual('Amount due', 'المتبقي')} value={doc.AmountDue} currency={currency} strong />
              )}
            </tbody>
          </table>
        </div>

{/*
          The client's own details, never ours. This document leaves the client
          for the client's customer: our address at the foot of it would send
          that customer's questions to the wrong firm and announce, on every
          invoice, who keeps the books. Empty until somebody sets it on the
          Branding screen — no footer is correct, somebody else's is not.
        */}
        {(footer.email || footer.phone || footer.address) && (
          <div style={{ marginTop: '34px', paddingTop: '12px', borderTop: '1px solid #e6e6e6', fontSize: '11px', color: '#777', display: 'flex', flexWrap: 'wrap', gap: '4px 14px' }}>
            <span>{organisation}</span>
            {footer.address && <span>{footer.address}</span>}
            {footer.email && <span dir="ltr">{footer.email}</span>}
            {footer.phone && (
              <span dir="ltr" style={{ fontFamily: theme.fontMono }}>
                {footer.phone}
              </span>
            )}
          </div>
        )}
      </div>
      )}
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: '10.5px', letterSpacing: '.06em', textTransform: 'uppercase', color: '#888', marginBottom: '3px' }}>
      {children}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <Label>{label}</Label>
      <div style={{ fontFamily: theme.fontMono, fontSize: '12.5px' }} dir="ltr">
        {value}
      </div>
    </div>
  );
}

function Th({ children, align = 'start', width }: { children: React.ReactNode; align?: 'start' | 'end'; width?: string }) {
  return (
    <th style={{ textAlign: align, padding: '7px 6px', fontSize: '10.5px', letterSpacing: '.05em', textTransform: 'uppercase', color: '#555', width }}>
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return (
    <td style={{ padding: '9px 6px', textAlign: 'end', fontFamily: theme.fontMono, verticalAlign: 'top' }} dir="ltr">
      {children}
    </td>
  );
}

function Total({
  label,
  value,
  currency,
  strong,
}: {
  label: string;
  value: number | undefined;
  currency: string;
  strong?: boolean;
}) {
  if (value === undefined) return null;
  return (
    <tr style={strong ? { borderTop: '2px solid #1a1a1a' } : undefined}>
      <td style={{ padding: '6px 10px', fontSize: '12.5px', fontWeight: strong ? 700 : 400 }}>{label}</td>
      <td
        style={{ padding: '6px 0', textAlign: 'end', fontFamily: theme.fontMono, fontWeight: strong ? 700 : 400, fontSize: strong ? '14px' : '12.5px' }}
        dir="ltr"
      >
        {formatMoney(value)} {currency}
      </td>
    </tr>
  );
}
